import { Button, Grid, TextField, MenuItem, DialogActions } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import AccountServices from 'services/AccountServices';
import VendorServices from 'services/VendorServices';
import CustomerService from 'services/CustomerService';
import ExpenseService from 'services/ExpenseService';
import { TRANSACTION_TYPES } from '../../const';
import { VendorContext } from 'Context/VendorContext';
import { ExpenseTypeContext } from 'Context/ExpenseTypeContext';
import { CustomerContext } from 'Context/CustomerContext';
import SearchSelect from 'components/utils/SearchSelect/SearchSelect';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';
import { ShopContext } from 'Context/ShopContext';
import ShopsHelper from 'helpers/ShopsHelper';

export default function CashInOutDialog({ defaultAccountHeadId, onSuccess, onClose }) {
  const { t: tCommon } = useTranslation('common');

  const [accountHeads, setAccountHeads] = useState([]);
  const [selectedHeadId, setSelectedHeadId] = useState(defaultAccountHeadId || '');
  const [toHeadId, setToHeadId] = useState('');
  const [type, setType] = useState(TRANSACTION_TYPES.IN); // 'in', 'out', 'transfer', 'expense', 'vendor_payment', 'customer_payment'
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const { vendors, getVendors } = useContext(VendorContext);
  const { expenseTypes, getExpenseTypes } = useContext(ExpenseTypeContext);
  const { customers, getCustomers } = useContext(CustomerContext);
  const [vendorId, setVendorId] = useState('');
  const [expenseTypeId, setExpenseTypeId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const { shop } = useContext(ShopContext);

  const { postAlertMessage } = useContext(AlertMessagesContext);

  useEffect(() => {
    const fetchHeads = async () => {
      try {
        const data = await AccountServices.getAccountHeads();
        const heads = data.results || data || [];
        setAccountHeads(heads);

        if (!selectedHeadId && heads.length > 0) {
          const cashHead = heads.find((h) => h.name.toLowerCase() === 'cash');
          if (cashHead) {
            setSelectedHeadId(cashHead.id);
          } else {
            setSelectedHeadId(heads[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load account heads', err);
      }
    };
    fetchHeads();
  }, [selectedHeadId]);

  useEffect(() => {
    const fetchVendorsAndExpenseTypes = async () => {
      try {
        await Promise.all([
          getCustomers(),
          getVendors(),
          getExpenseTypes()
        ]);
      } catch (err) {
        console.error('Failed to load additional data', err);
      }
    };
    fetchVendorsAndExpenseTypes();
  }, [getVendors, getExpenseTypes, getCustomers]);

  useEffect(() => {
    setVendorId('');
    setExpenseTypeId('');
    setCustomerId('');
    setToHeadId('');
  }, [type]);

  const handleCustomerPayment = async (finalDescription) => {
    if (!customerId) {
      postAlertMessage({ text: tCommon('Please select a Customer'), type: 'failed' });
      return;
    }

    const payload = {
      credit: Number(amount),
      description: finalDescription,
      note: finalDescription,
      payment_modes: [{ account_head_id: selectedHeadId, amount: Number(amount) }]
    };

    setLoadingIndicator(true);
    try {
      await CustomerService.addPaymentToCustomer(customerId, payload);
      postAlertMessage({ text: tCommon('Transaction recorded successfully'), type: 'success' });
      onSuccess();
    } catch (err) {
      postAlertMessage({ text: err.message || tCommon('Failed to record transaction'), type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleVendorPayment = async (finalDescription) => {
    if (!vendorId) {
      postAlertMessage({ text: tCommon('Please select a Vendor'), type: 'failed' });
      return;
    }

    const payload = {
      debit: Number(amount),
      description: finalDescription,
      payment_modes: [{ account_head_id: selectedHeadId, amount: Number(amount) }]
    };

    setLoadingIndicator(true);
    try {
      await VendorServices.addPaymentToVendor(vendorId, payload);
      postAlertMessage({ text: tCommon('Transaction recorded successfully'), type: 'success' });
      onSuccess();
    } catch (err) {
      postAlertMessage({ text: err.message || tCommon('Failed to record transaction'), type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleExpense = async (finalDescription) => {
    if (!expenseTypeId) {
      postAlertMessage({ text: tCommon('Please select an Expense Type'), type: 'failed' });
      return;
    }

    const payload = {
      amount: Number(amount),
      description: finalDescription,
      date,
      type_id: expenseTypeId,
      tenders: [{ account_head_id: selectedHeadId, amount: Number(amount) }]
    };
    
    if (vendorId) {
      payload.vendor_id = vendorId;
    }

    setLoadingIndicator(true);
    try {
      await ExpenseService.createExpense(payload);
      postAlertMessage({ text: tCommon('Transaction recorded successfully'), type: 'success' });
      onSuccess();
    } catch (err) {
      postAlertMessage({ text: err.message || tCommon('Failed to record transaction'), type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleLedgerEntry = async (finalDescription) => {
    let payload = {};
    if (type === TRANSACTION_TYPES.TRANSFER) {
      payload = {
        from_account_id: selectedHeadId,
        to_account_id: toHeadId,
        amount: Number(amount),
        type: TRANSACTION_TYPES.TRANSFER,
        description: finalDescription,
        date,
      };
    } else {
      payload = {
        account_head_id: selectedHeadId,
        amount: Number(amount),
        type,
        description: finalDescription,
        date,
      };
    }

    setLoadingIndicator(true);
    try {
      await AccountServices.createLedgerEntry(payload);
      postAlertMessage({ text: tCommon('Transaction recorded successfully'), type: 'success' });
      onSuccess();
    } catch (err) {
      postAlertMessage({ text: err.message || tCommon('Failed to record transaction'), type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      postAlertMessage({ text: tCommon('Please enter a valid amount'), type: 'failed' });
      return;
    }

    if (type === TRANSACTION_TYPES.TRANSFER){
      if (!selectedHeadId || !toHeadId){
        postAlertMessage({ text: tCommon('Please select both Source and Destination accounts'), type: 'failed' });
        return;
      }
      if (selectedHeadId === toHeadId) {
        postAlertMessage({ text: tCommon('Source and Destination accounts must be different'), type: 'failed' });
        return;
      }
    } else if (!selectedHeadId) {
        postAlertMessage({ text: tCommon('Please select a valid account head'), type: 'failed' });
        return;
    }

    const accountName = accountHeads.find((h) => h.id === selectedHeadId)?.name || '';
    let autoDesc = '';

    if (type === TRANSACTION_TYPES.TRANSFER) {
      const toName = accountHeads.find((h) => h.id === toHeadId)?.name || '';
      autoDesc = tCommon('Balance Transfer from {{from}} to {{to}}', { from: accountName, to: toName });
    } else if (type === TRANSACTION_TYPES.VENDOR_PAYMENT) {
      autoDesc = tCommon('Vendor Payment from {{account}}', { account: accountName });
    } else if (type === TRANSACTION_TYPES.CUSTOMER_PAYMENT) {
      autoDesc = tCommon('Customer Payment deposited to {{account}}', { account: accountName });
    } else if (type === TRANSACTION_TYPES.EXPENSE || type === TRANSACTION_TYPES.OUT) {
      autoDesc = tCommon('Withdrawal from {{account}}', { account: accountName });
    } else if (type === TRANSACTION_TYPES.IN) {
      autoDesc = tCommon('Deposit to {{account}}', { account: accountName });
    }

    const finalDescription = description.trim() ? `${autoDesc} - ${description.trim()}` : autoDesc;

    if (type === TRANSACTION_TYPES.VENDOR_PAYMENT) {
      await handleVendorPayment(finalDescription);
    } else if (type === TRANSACTION_TYPES.CUSTOMER_PAYMENT) {
      await handleCustomerPayment(finalDescription);
    } else if (type === TRANSACTION_TYPES.EXPENSE) {
      await handleExpense(finalDescription);
    } else {
      await handleLedgerEntry(finalDescription);
    }
  };

  const getHelperText = (headId, isFromAccount) => {
    const head = accountHeads.find((h) => h.id === headId);
    if (!head) return '';
    const currentBalance = Number(head.balance || 0);
    const numAmount = Number(amount || 0);
    let updatedBalance = currentBalance;

    if (type === TRANSACTION_TYPES.TRANSFER) {
      if (isFromAccount) {
        updatedBalance = currentBalance - numAmount;
      } else {
        updatedBalance = currentBalance + numAmount;
      }
    } else if (type === TRANSACTION_TYPES.IN || type === TRANSACTION_TYPES.CUSTOMER_PAYMENT) {
      updatedBalance = currentBalance + numAmount;
    } else if (type === TRANSACTION_TYPES.OUT || type === TRANSACTION_TYPES.EXPENSE || type === TRANSACTION_TYPES.VENDOR_PAYMENT) {
      updatedBalance = currentBalance - numAmount;
    }

    return `${tCommon('Current')}: ${currentBalance.toFixed(2)}  |  ${tCommon('Updated')}: ${updatedBalance.toFixed(
      2
    )}`;
  };

  return (
    <div className={styles.popUp} style={{ zIndex: 1300 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>
            {type === TRANSACTION_TYPES.TRANSFER ? tCommon('Account Balance Transfer') : tCommon('Deposit / Withdrawal')}
          </h2>
          <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} />
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="top" spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={tCommon('Transaction Type')}
                variant="outlined"
                size="small"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={styles.selectBox}
                fullWidth
              >
                <MenuItem value={TRANSACTION_TYPES.IN}>{tCommon('Deposit')}</MenuItem>
                <MenuItem value={TRANSACTION_TYPES.OUT}>{tCommon('Withdrawal')}</MenuItem>
                <MenuItem value={TRANSACTION_TYPES.TRANSFER}>{tCommon('Balance Transfer')}</MenuItem>
                <MenuItem value={TRANSACTION_TYPES.EXPENSE}>{tCommon('Expense')}</MenuItem>
                <MenuItem value={TRANSACTION_TYPES.VENDOR_PAYMENT}>{tCommon('Vendor Payment')}</MenuItem>
                <MenuItem value={TRANSACTION_TYPES.CUSTOMER_PAYMENT}>{tCommon('Customer Payment')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Amount')}
                className={styles.numberInput}
                name="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={[TRANSACTION_TYPES.TRANSFER, TRANSACTION_TYPES.VENDOR_PAYMENT, TRANSACTION_TYPES.CUSTOMER_PAYMENT].includes(type) ? 6 : 12}>
              <TextField
                select
                label={type === TRANSACTION_TYPES.TRANSFER ? tCommon('From Account') : tCommon('Account Head')}
                variant="outlined"
                size="small"
                value={selectedHeadId}
                onChange={(e) => setSelectedHeadId(e.target.value)}
                className={styles.selectBox}
                fullWidth
                helperText={selectedHeadId ? getHelperText(selectedHeadId, true) : ''}
                FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
              >
                {accountHeads.map((head) => (
                  <MenuItem key={head.id} value={head.id}>
                    {head.name} ({head.type})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {type === TRANSACTION_TYPES.TRANSFER && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={tCommon('To Account')}
                  variant="outlined"
                  size="small"
                  value={toHeadId}
                  onChange={(e) => setToHeadId(e.target.value)}
                  className={styles.selectBox}
                  fullWidth
                  helperText={toHeadId ? getHelperText(toHeadId, false) : ''}
                  FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
                >
                    {accountHeads.map((head) => (
                      <MenuItem key={head.id} value={head.id} disabled={head.id === selectedHeadId}>
                        {head.name} ({head.type})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {(type === TRANSACTION_TYPES.EXPENSE || type === TRANSACTION_TYPES.VENDOR_PAYMENT) && (
                <>
                  {type === TRANSACTION_TYPES.EXPENSE && (
                    <Grid item xs={12} sm={6}>
                      <SearchSelect
                        label={tCommon('Expense Type')}
                        options={expenseTypes || []}
                        value={expenseTypeId}
                        onChange={(val) => setExpenseTypeId(val || '')}
                        placeholder={tCommon('Search Expense Type...')}
                        className={styles.selectBox}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <SearchSelect
                      label={tCommon('Vendor')}
                      options={
                        type === TRANSACTION_TYPES.EXPENSE
                          ? [{ id: '', name: tCommon('None') }, ...vendors]
                          : vendors
                      }
                      value={vendorId}
                      onChange={(val) => setVendorId(val || '')}
                      placeholder={tCommon('Search Vendor...')}
                      className={styles.selectBox}
                      helperText={
                        vendorId && vendors.find((v) => v.id === vendorId) && (() => {
                          const selectedVendor = vendors.find((v) => v.id === vendorId);
                          return (
                            <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <span style={{ color: (selectedVendor.balance || 0) < 0 ? 'red' : 'inherit' }}>
                                {tCommon('Current Balance')}: {ShopsHelper.getAmountFormatted(shop, selectedVendor.balance || 0)}
                              </span>
                              {', '}
                              <span style={{ color: (selectedVendor.pending_amount || 0) < 0 ? 'red' : 'inherit' }}>
                                {tCommon('Pending Amount')}: {ShopsHelper.getAmountFormatted(shop, selectedVendor.pending_amount || 0)}
                              </span>
                            </span>
                          );
                        })()
                      }
                      FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
                    />
                  </Grid>
                </>
              )}
              {type === TRANSACTION_TYPES.CUSTOMER_PAYMENT && (
                <Grid item xs={12} sm={6}>
                  <SearchSelect
                    label={tCommon('Customer')}
                    options={customers || []}
                    value={customerId}
                    onChange={(val) => setCustomerId(val || '')}
                    placeholder={tCommon('Search Customer...')}
                    className={styles.selectBox}
                    helperText={
                      customerId && customers.find((c) => c.id === customerId) && (() => {
                        const selectedCustomer = customers.find((c) => c.id === customerId);
                        return (
                          <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span style={{ color: (selectedCustomer.credit_limit || 0) < 0 ? 'red' : 'inherit' }}>
                              {tCommon('Credit Limit')}: {formatBalanceWithCr(selectedCustomer.credit_limit || 0)}
                            </span>
                            {', '}
                            <span style={{ color: (selectedCustomer.balance || 0) < 0 ? 'red' : 'inherit' }}>
                              {tCommon('Balance')}: {formatBalanceWithCr(selectedCustomer.balance || 0)}
                            </span>
                            {', '}
                            <span style={{ color: (selectedCustomer.points || 0) < 0 ? 'red' : 'inherit' }}>
                              {tCommon('Points')}: {selectedCustomer.points || 0}
                            </span>
                          </span>
                        );
                      })()
                    }
                    FormHelperTextProps={{ style: { fontWeight: 500, color: '#00a65a' } }}
                  />
                </Grid>
              )}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Transaction Date')}
                    type="date"
                    variant="outlined"
                    size="small"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Description')}
                    placeholder={
                      type === TRANSACTION_TYPES.IN
                        ? tCommon('e.g. Owner Investment, Cash Deposit')
                        : type === TRANSACTION_TYPES.OUT
                        ? tCommon('e.g. Personal Drawings, Petty Cash Draw')
                        : tCommon('e.g. Cash collection deposit to bank/UPI')
                    }
                    variant="outlined"
                    size="small"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" size="small" onClick={onClose}>
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              disabled={
                amount === '' ||selectedHeadId === '' ||(type === TRANSACTION_TYPES.TRANSFER && toHeadId === '') ||(type === TRANSACTION_TYPES.EXPENSE && expenseTypeId === '') ||(type === TRANSACTION_TYPES.VENDOR_PAYMENT && vendorId === '') ||(type === TRANSACTION_TYPES.CUSTOMER_PAYMENT && customerId === '') ||loadingIndicator
              }
              onClick={handleSubmit}
            >
              {tCommon('Save Transaction')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
